<template>
    <div v-if="snoozed" @click="unsnoozeThread">
        <slot :snoozing="snoozing" :snoozed="snoozed" />
    </div>

    <vx-dropdown v-else :placement="props.placement" :teleport="props.teleport">
        <slot :snoozing="snoozing" :snoozed="snoozed" />
        <template v-slot:items>
            <vx-dropdown-item
                v-for="preset in snoozePresets"
                :key="preset.label"
                @click="snoozeThread(preset.getTime())"
            >
                {{ preset.label }}
            </vx-dropdown-item>
            <vx-dropdown-item @click="showCustomSnooze = true"> Custom... </vx-dropdown-item>
        </template>
    </vx-dropdown>

    <vx-modal :visible="showCustomSnooze" @update:visible="showCustomSnooze = $event" :padding="false" size="md">
        <template v-slot:header>
            <div class="px-6 pt-6">Snooze Thread</div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">Choose when this thread should reappear.</div>
        </template>

        <form :id="formId" @submit.prevent="snoozeCustom" class="px-6 py-12 flex flex-col items-center gap-2">
            <div v-if="customSnoozeDate" class="text-center text-sm text-slate-700">
                {{ customSnoozeDateFormatted }}
            </div>
            <vx-date-picker
                :key="minDateKey"
                v-model="customSnoozeDate"
                mode="dateTime"
                :timezone="getClientTz()"
                :minDate="minDate"
                isRequired
            />
        </form>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click="showCustomSnooze = false" size="lg" color="muted">
                    Cancel
                    <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
                <vx-button
                    :form="formId"
                    type="submit"
                    size="lg"
                    color="primary"
                    class="lg:grow"
                    :loading="snoozing"
                    :disabled="!customSnoozeDate"
                >
                    Snooze
                    <font-awesome-icon widthAuto :icon="faClock" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { faCircleX, faClock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxDatePicker, VxDropdown, VxDropdownItem, VxModal } from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import { computed, ref, useId, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { threadsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import dayjs from '~/utils/dayjs';
import { formatTz, getClientTz } from '~/utils/date';
import Bus from '~/globals/eventBus';
import type { Thread } from '~/services/threads/threadsClient.types';

const props = withDefaults(
    defineProps<{
        thread: Thread;
        placement?: 'left' | 'right';
        teleport?: boolean;
    }>(),
    {
        placement: 'right',
        teleport: false,
    }
);

const emit = defineEmits<{
    snoozed: [];
    unsnoozed: [];
}>();

const snoozed = computed(() => props.thread.thread_section === 'snoozed');

const formId = useId();
const toasted = useToasted();
const snoozing = ref(false);
const showCustomSnooze = ref(false);
const customSnoozeDate = ref<Date | null>(null);

const getDefaultCustomSnoozeDate = () => {
    return dayjs().tz(getClientTz()).add(1, 'hour').startOf('hour').toDate();
};

watch(showCustomSnooze, (visible) => {
    if (visible) {
        customSnoozeDate.value = getDefaultCustomSnoozeDate();
    }
});

const minDate = computed(() => dayjs().tz(getClientTz()).add(2, 'minutes').toDate());
const minDateKey = useInterval(60000);

const customSnoozeDateFormatted = computed(() => {
    if (!customSnoozeDate.value) {
        return '';
    }
    const timezone = dayjs(customSnoozeDate.value).tz(getClientTz()).format('z');
    return (
        dayjs(customSnoozeDate.value).tz(getClientTz()).format('MMM DD, YYYY [at] h:mm a') + ' ' + formatTz(timezone)
    );
});

const snoozePresets = computed(() => {
    const timezone = getClientTz();
    return [
        { label: '1 hour', getTime: () => dayjs().tz(timezone).add(1, 'hour') },
        { label: '3 hours', getTime: () => dayjs().tz(timezone).add(3, 'hours') },
        {
            label: 'Tomorrow 9 AM',
            getTime: () => dayjs().tz(timezone).add(1, 'day').startOf('day').add(9, 'hours'),
        },
        {
            label: 'Next week',
            getTime: () => dayjs().tz(timezone).add(1, 'week').startOf('isoWeek').add(9, 'hours'),
        },
    ];
});

const snoozeThread = async (snoozeUntil: dayjs.Dayjs): Promise<void> => {
    if (snoozing.value) {
        return;
    }
    snoozing.value = true;
    try {
        const context = { teamId: props.thread.team_id, groupId: props.thread.group_id };
        await threadsClient.snooze(context, props.thread.contact_id, {
            snooze_until: snoozeUntil.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
        });
        const timezone = snoozeUntil.tz(getClientTz()).format('z');
        toasted.success(
            `Thread snoozed until ${snoozeUntil.tz(getClientTz()).format('MMM DD, YYYY [at] h:mm a')} ${formatTz(
                timezone
            )}.`
        );
        showCustomSnooze.value = false;
        customSnoozeDate.value = null;
        Bus.$emit('threadUpdated', { thread: { ...props.thread, thread_section: 'snoozed' } });
        emit('snoozed');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        snoozing.value = false;
    }
};

const snoozeCustom = (): void => {
    if (!customSnoozeDate.value) {
        return;
    }
    const snoozeUntil = dayjs(customSnoozeDate.value).tz(getClientTz());
    if (!snoozeUntil.isValid()) {
        toasted.error('Please enter a valid date and time.');
        return;
    }
    if (snoozeUntil.isBefore(dayjs().add(60, 'seconds'))) {
        toasted.error('Snooze time must be at least 1 minute in the future.');
        return;
    }
    snoozeThread(snoozeUntil);
};

const unsnoozeThread = async (): Promise<void> => {
    if (snoozing.value) {
        return;
    }
    snoozing.value = true;
    try {
        const context = { teamId: props.thread.team_id, groupId: props.thread.group_id };
        await threadsClient.snooze(context, props.thread.contact_id, { snooze_until: null });
        toasted.success('Snooze removed.');
        Bus.$emit('threadUpdated', { thread: { ...props.thread, thread_section: 'pending' } });
        emit('unsnoozed');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        snoozing.value = false;
    }
};
</script>
